import { CSSProperties, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Input, notification, Row, Select, Space, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { FaUniversity } from "react-icons/fa";

import { IFonteACadastrar } from "../../../../../../FontesACadastrar/interfaces";
import { ICategoria, IFonteSaldoDeContas } from "../../../../../interfaces";
import { ICreateFonte, IOrdenacaoFonteItem, IUpdateCategoriaFonte, IUpdateDescricaoFonte } from "../../interfaces";

import { EmitirErro } from "../../../../../../../../services/EmitirErro";
import SolicitacoesDeAtivacaoService from "../../../../../../../../services/http/services/SaldoDeContas/SolicitacoesDeAtivacaoService";
import FontesACadastrarService from "../../../../../../../../services/http/services/SaldoDeContas/FontesACadastrarService";
import CardInformacao from "../../../../../../components/CardInformacao";

export interface IFonteFormProps {
    onCancel: () => void;
    solicitacaoId?: number;
    onFinish: (fonte: IFonteSaldoDeContas, fonteACadastrarId?: number) => void;
    onOnlyCategoriaUpdated?: (fonte: { numero: string, categoriaId: number }) => void;
    onUpdateCategoriaFonte?: (data: IUpdateCategoriaFonte) => Promise<void>;
    onUpdateDescricaoFonte?: (data: IUpdateDescricaoFonte) => Promise<IFonteSaldoDeContas | null>;
    onCreateFonte?: (data: ICreateFonte) => Promise<IFonteSaldoDeContas | null>;
    fonte?: IOrdenacaoFonteItem;
    initialCategoriaId?: number | null;
    cardStyle?: CSSProperties;
    cardBodyStyle?: CSSProperties;
    onlyDescricao?: boolean;
    onlyCategoria?: boolean;
    fonteACadastrar?: IFonteACadastrar | null;
}

export default function FonteForm({
    onCancel,
    solicitacaoId,
    onFinish,
    fonte,
    initialCategoriaId,
    cardStyle,
    cardBodyStyle,
    onlyDescricao,
    onlyCategoria,
    onOnlyCategoriaUpdated,
    onUpdateCategoriaFonte,
    onUpdateDescricaoFonte,
    onCreateFonte,
    fonteACadastrar,
}: IFonteFormProps) {
    const [categorias, setCategorias] = useState<ICategoria[] | null>(null);
    const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
    const [numero, setNumero] = useState<string>(fonte?.numero || fonteACadastrar?.numero || '');
    const [descricao, setDescricao] = useState<string>(fonte?.descricao || '');
    const [categoria, setCategoria] = useState<number | null>(initialCategoriaId || null);
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);

    const disabledFieldsByFonteACadastrar = !!fonteACadastrar;

    const handleSubmit = async () => {
        try {
            if (!categoria) {
                return;
            }

            setIsSubmiting(true);

            if (fonteACadastrar) {

                const createdFonte = await FontesACadastrarService.createFonte({
                    numero,
                    descricao,
                    categoriaId: categoria,
                    orgaoId: fonteACadastrar.orgaoId,
                });

                setIsSubmiting(false);
                onFinish(createdFonte, fonteACadastrar.id);

                notification.success({
                    message: 'Sucesso',
                    description:
                        'Fonte cadastrada com sucesso!',
                });

                return;
            }

            if (!solicitacaoId) {
                setIsSubmiting(false);
                return;
            }

            if (fonte && onlyDescricao && onUpdateDescricaoFonte) {
                const isGroup = numero.split(',').length > 1;

                const updatedFonte = await onUpdateDescricaoFonte({
                    descricao,
                    numero,
                    solicitacaoId,
                    isGroup,
                });

                setIsSubmiting(false);
                updatedFonte && onFinish(updatedFonte);

                notification.success({
                    message: 'Sucesso',
                    description: `${isGroup ? 'Nome do grupo atualizado' : 'Descrição da fonte atualizada'} com sucesso!`,
                });

                return;
            }

            if (fonte && onlyCategoria && onUpdateCategoriaFonte) {
                await onUpdateCategoriaFonte({
                    categoriaId: categoria,
                    numero,
                    solicitacaoId,
                });

                setIsSubmiting(false);
                onOnlyCategoriaUpdated && onOnlyCategoriaUpdated({ numero, categoriaId: categoria });

                notification.success({
                    message: 'Sucesso',
                    description:
                        'Categoria da fonte atualizada com sucesso!',
                });

                return;
            }

            const createdFonte = await onCreateFonte?.({
                categoriaId: categoria,
                descricao,
                numero,
                solicitacaoId,
            });

            if (!createdFonte) {
                return;
            }

            setIsSubmiting(false);
            onFinish(createdFonte);
        } catch (error) {
            EmitirErro(error, true, 'createFonte_20aasdnmasd0inoj2390jknasd');
            setIsSubmiting(false);
        }
    };

    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            try {
                setIsLoadingData(true);

                const [
                    categoriasData,
                ] = await Promise.all([
                    SolicitacoesDeAtivacaoService.getCategorias({ signal: controller.signal }),
                ]);

                setCategorias(categoriasData);
                setIsLoadingData(false);
            } catch (error) {
                EmitirErro(error, true, 'loadFonteFormData_20aasdnmasd0inoj2390jknasd');
                setIsLoadingData(false);
            }
        }

        loadData();

        return () => {
            controller.abort();
        }
    }, []);

    useEffect(() => {
        setNumero(fonte?.numero || fonteACadastrar?.numero || '');
        setDescricao(fonte?.descricao || '');
        setCategoria(initialCategoriaId || null);
    }, [fonte, fonteACadastrar]);

    const disableEditNumeroInput = useMemo(() => {
        return disabledFieldsByFonteACadastrar || (!!fonte && (onlyDescricao || onlyCategoria));
    }, [disabledFieldsByFonteACadastrar, fonte, onlyDescricao, onlyCategoria]);

    const disableEditCategoriaInput = useMemo(() => !!fonte && onlyDescricao, [fonte, onlyDescricao]);
    const disableEditDescricaoInput = useMemo(() => !!fonte && onlyCategoria, [fonte, onlyCategoria]);

    return (
        <Card
            size="small"
            bordered
            className={(!fonte && !fonteACadastrar) ? "cardFormFonteSaldoDeContas" : undefined}
            style={{
                marginTop: 16,
                borderRadius: 8,
                ...cardStyle,
            }}
            bodyStyle={cardBodyStyle}
        >
            {
                !fonte && !fonteACadastrar && (
                    <Space
                        align="center"
                        style={{ width: '100%', justifyContent: 'space-between' }}
                    >
                        <Typography.Text strong>
                            Nova fonte
                        </Typography.Text>

                        <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={onCancel}
                            disabled={isSubmiting}
                        />
                    </Space>
                )
            }

            <Form
                layout="vertical"
                style={{ marginTop: 16 }}
                component={false}
            >
                {
                    fonteACadastrar && (
                        <CardInformacao
                            IconComponent={FaUniversity}
                            title="ÓRGÃO"
                            value={fonteACadastrar.orgao?.nome || "--"}
                            iconSize={13}
                            fontSize={12}
                            style={{
                                padding: '4px 6px',
                                marginBottom: 12,
                            }}
                        />
                    )
                }

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Número"
                            required
                        >
                            <Input
                                placeholder="Número da fonte"
                                value={numero}
                                onChange={(e) => setNumero(e.target.value)}
                                readOnly={disableEditNumeroInput}
                                disabled={disableEditNumeroInput}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item label="Categoria" required>
                            <Select
                                placeholder="Selecione..."
                                options={categorias?.map((e) => ({ value: e.id, label: e.nome }))}
                                loading={isLoadingData}
                                value={categoria}
                                onChange={(value) => setCategoria(value)}
                                disabled={disableEditCategoriaInput}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={24}>
                        <Form.Item
                            label="Descrição"
                            required
                        >
                            <Input
                                placeholder="Descrição da fonte"
                                value={descricao}
                                onChange={(e) => setDescricao(e.target.value)}
                                readOnly={disableEditDescricaoInput}
                                disabled={disableEditDescricaoInput}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button
                        onClick={onCancel}
                        disabled={isSubmiting}
                    >
                        Cancelar
                    </Button>

                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        disabled={!numero || !descricao || !categoria}
                        loading={isSubmiting}
                    >
                        Salvar fonte
                    </Button>
                </Space>
            </Form>
        </Card>
    )
}
